const ACTIONS_MAPPING = {
    left: { keys: ['ArrowLeft'], selector: '.sketchfab-360-viewer-controls__button--left' },
    right: { keys: ['ArrowRight'], selector: '.sketchfab-360-viewer-controls__button--right' },
    up: { keys: ['ArrowUp'], selector: '.sketchfab-360-viewer-controls__button--up' },
    down: { keys: ['ArrowDown'], selector: '.sketchfab-360-viewer-controls__button--down' },
    zoomIn: { keys: ['=', '+'], selector: '.sketchfab-360-viewer-controls__button--zoom-in' },
    zoomOut: { keys: ['-', '_'], selector: '.sketchfab-360-viewer-controls__button--zoom-out' },
};

const CLASS_NAMES = {
    DRAG_TO_ROTATE: '.drag-to-rotate',
    SKETCHFAB_360_VIEWER: '.sketchfab-360-viewer',
    CONTROLS: '.sketchfab-360-viewer-controls',
    CONTROLS_BUTTON_EXPAND: '.sketchfab-360-viewer-controls__button--expand',
};

const STATE_CLASSES = {
    DISPLAY_NONE: 'display-none',
    HIDE: 'hide',
    CONTROLS_BUTTON_HIDDEN: 'sketchfab-360-viewer-controls__button--hidden',
    CONTROLS_COLLAPSED: 'sketchfab-360-viewer-controls--collapsed',
};

const ROTATION_STEP = 0.33;

export default class SketchfabViewer {
    constructor({ colorSwatches, parentElement }) {
        this.parentElement = parentElement;
        this.colorSwatches = colorSwatches;
        this.elements = {
            dragToRotate: parentElement.querySelector(CLASS_NAMES.DRAG_TO_ROTATE),
            sketchfabViewer: parentElement.querySelector(CLASS_NAMES.SKETCHFAB_360_VIEWER),
            iframe: parentElement.querySelector('iframe'),
            controls: parentElement.querySelector(CLASS_NAMES.CONTROLS),
            expandButton: parentElement.querySelector(CLASS_NAMES.CONTROLS_BUTTON_EXPAND),
        };
        this.state = {
            isControlsExpanded: this.elements.controls
                ? !this.elements.controls.classList.contains(STATE_CLASSES.CONTROLS_COLLAPSED)
                : false,
            isViewerReady: false,
        };
        this.init();
    }

    init() {
        this.disableIFrameFocus();
        this.onReady();
        this.initKeyboardNavigation();
        this.initOnScreenControls();
        this.setupColorSwatches();
        this.setupDragToRotateEvents();
        this.onUserInteraction();
    }

    setupColorSwatches() {
        this.colorSwatches.forEach((colorSwatch, index) => {
            colorSwatch.addEventListener('click', () => {
                const colorValue = colorSwatch.getAttribute('color-id');
                this.setColorId(colorValue);
            });

            if (index === 0) {
                colorSwatch.click();
            }
        });
    }

    setupDragToRotateEvents() {
        if (!this.elements.dragToRotate) return;

        ['touchstart', 'click'].forEach((event) => {
            this.elements.dragToRotate.addEventListener(event, () => {
                if (this.state.isViewerReady) {
                    this.hideDragToRotate();
                }
            });
        });
    }

    // eslint-disable-next-line class-methods-use-this
    setColorId(colorId) {
        window.Sketchfab360Viewer.setColorId(colorId);
    }

    disableIFrameFocus() {
        if (this.elements.iframe) {
            this.elements.iframe.setAttribute('tabindex', '-1');
            this.elements.iframe.setAttribute('aria-hidden', 'true');
        }
    }

    onReady() {
        window.Sketchfab360Viewer.onReady = () => {
            this.state.isViewerReady = true;
            if (this.elements.dragToRotate) {
                this.elements.dragToRotate.classList.remove(STATE_CLASSES.HIDE);
            }
            if (this.elements.iframe && this.elements.sketchfabViewer) {
                this.elements.iframe.title = this.elements.sketchfabViewer.dataset.iframeLabel;
            }
            this.showOnScreenControls();
        };
    }

    initKeyboardNavigation() {
        // Define key actions mapping
        const keyActions = Object.entries(ACTIONS_MAPPING).reduce((acc, [action, { keys }]) => {
            keys.forEach((key) => {
                acc[key] = action;
            });
            return acc;
        }, {});

        this.parentElement.addEventListener('keydown', (e) => {
            const direction = keyActions[e.key];
            if (direction) {
                e.preventDefault();
                this.handleRotation(direction);
            }
        });
    }

    initOnScreenControls() {
        if (!this.elements.controls) {
            console.error('Failed to attach color picker controls.');
            return;
        }

        this.elements.controls.addEventListener('click', (e) => {
            if (e.target.closest(CLASS_NAMES.CONTROLS_BUTTON_EXPAND)) {
                this.toggleControls();
            } else {
                Object.entries(ACTIONS_MAPPING).some(([direction, { selector }]) => {
                    if (e.target.closest(selector)) {
                        this.handleRotation(direction);
                        return true;
                    }
                    return false;
                });
            }
        });
    }

    toggleControls() {
        this.elements.controls.classList.toggle(STATE_CLASSES.CONTROLS_COLLAPSED, this.state.isControlsExpanded);
        this.state.isControlsExpanded = !this.state.isControlsExpanded;
        this.updateExpandButtonAttributes();
    }

    updateExpandButtonAttributes() {
        if (!this.elements.expandButton) return;

        this.elements.expandButton.setAttribute('aria-expanded', this.state.isControlsExpanded);

        const label = this.state.isControlsExpanded
            ? this.elements.expandButton.dataset.accessibilityCollapseLabel
            : this.elements.expandButton.dataset.accessibilityExpandLabel;

        this.elements.expandButton.setAttribute('aria-label', label);
    }

    // eslint-disable-next-line class-methods-use-this
    handleRotation(direction) {
        window.Sketchfab360Viewer.controlCamera(direction, ROTATION_STEP);
    }

    onUserInteraction() {
        window.Sketchfab360Viewer.onUserInteraction = () => {
            if (this.elements.dragToRotate) {
                const isDragToRotateVisible = !this.elements.dragToRotate.classList.contains(STATE_CLASSES.HIDE);
                if (isDragToRotateVisible) {
                    this.elements.dragToRotate.classList.add(STATE_CLASSES.HIDE);
                }
            }
        };
    }

    hideDragToRotate() {
        if (this.elements.dragToRotate) {
            this.elements.dragToRotate.classList.add(STATE_CLASSES.HIDE);
        }
    }

    showOnScreenControls() {
        if (this.elements.expandButton) {
            this.elements.expandButton.classList.remove(STATE_CLASSES.CONTROLS_BUTTON_HIDDEN);
        }
    }
}
